import { useEffect, useRef, useState, useCallback } from 'react';
import maplibregl, { Map as MapLibreMap, Marker } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Device, DevicePosition, OemVehicleState, consumeChangedImeis, shiftPositionQueue, peekPositionQueue, getPositionQueueLength, getPositionQueue, clearPositionQueue } from '../store/devices.store';
import { useSettingsStore, MapStyle, MAP_STYLES } from '../store/settings.store';
import { useAuthStore } from '../store/auth.store';
import { devicesApi, geocodingApi } from '../services/api';
import { ZoomIn, ZoomOut, Locate, Maximize2, Layers, Share2, Lock, Unlock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import ShareLinkModal from './ShareLinkModal';

// Enabled tracker parameter interface
interface EnabledParameter {
  id: string;
  code: string;
  name: string;
  description?: string;
  category: string;
  dataType: string;
  unit?: string;
  icon?: string;
  color?: string;
  avlId?: number;
  requiresObd: boolean;
  requiresBluetooth?: boolean;
}

// Tracker capabilities interface
interface TrackerCapabilities {
  hasObd: boolean;
  hasBluetooth: boolean;
  hasCan: boolean;
  has4G: boolean;
  hasCamera: boolean;
  hasAccelerometer: boolean;
  category: string;
}

// Teltonika tracker capabilities mapping
const trackerCapabilities: Record<string, TrackerCapabilities> = {
  // OBD Series (2G) - All have OBD
  'FMB001': { hasObd: true, hasBluetooth: false, hasCan: false, has4G: false, hasCamera: false, hasAccelerometer: true, category: 'OBD' },
  'FMB002': { hasObd: true, hasBluetooth: false, hasCan: false, has4G: false, hasCamera: false, hasAccelerometer: true, category: 'OBD' },
  'FMB003': { hasObd: true, hasBluetooth: false, hasCan: false, has4G: false, hasCamera: false, hasAccelerometer: true, category: 'OBD' },
  'FMB010': { hasObd: true, hasBluetooth: false, hasCan: false, has4G: false, hasCamera: false, hasAccelerometer: true, category: 'OBD' },
  'FMB020': { hasObd: true, hasBluetooth: false, hasCan: false, has4G: false, hasCamera: false, hasAccelerometer: true, category: 'OBD' },

  // Professional Series (2G) - CAN support, some with Bluetooth
  'FMB110': { hasObd: false, hasBluetooth: false, hasCan: true, has4G: false, hasCamera: false, hasAccelerometer: true, category: 'Professional' },
  'FMB120': { hasObd: false, hasBluetooth: true, hasCan: true, has4G: false, hasCamera: false, hasAccelerometer: true, category: 'Professional' },
  'FMB122': { hasObd: false, hasBluetooth: true, hasCan: true, has4G: false, hasCamera: false, hasAccelerometer: true, category: 'Professional' },
  'FMB125': { hasObd: false, hasBluetooth: true, hasCan: true, has4G: false, hasCamera: false, hasAccelerometer: true, category: 'Professional' },
  'FMB130': { hasObd: false, hasBluetooth: false, hasCan: true, has4G: false, hasCamera: false, hasAccelerometer: true, category: 'Professional' },
  'FMB140': { hasObd: false, hasBluetooth: false, hasCan: true, has4G: false, hasCamera: false, hasAccelerometer: true, category: 'Professional' },

  // Easy Series (2G) - Basic trackers
  'FMB202': { hasObd: false, hasBluetooth: false, hasCan: false, has4G: false, hasCamera: false, hasAccelerometer: true, category: 'Easy' },
  'FMB204': { hasObd: false, hasBluetooth: false, hasCan: false, has4G: false, hasCamera: false, hasAccelerometer: true, category: 'Easy' },
  'FMB208': { hasObd: false, hasBluetooth: false, hasCan: false, has4G: false, hasCamera: false, hasAccelerometer: true, category: 'Easy' },
  'FMB209': { hasObd: false, hasBluetooth: false, hasCan: false, has4G: false, hasCamera: false, hasAccelerometer: true, category: 'Easy' },

  // GNSS Series (2G)
  'FMB900': { hasObd: false, hasBluetooth: false, hasCan: false, has4G: false, hasCamera: false, hasAccelerometer: true, category: 'GNSS' },
  'FMB910': { hasObd: false, hasBluetooth: true, hasCan: false, has4G: false, hasCamera: false, hasAccelerometer: true, category: 'GNSS' },
  'FMB920': { hasObd: false, hasBluetooth: true, hasCan: true, has4G: false, hasCamera: false, hasAccelerometer: true, category: 'GNSS' },
  'FMB930': { hasObd: false, hasBluetooth: false, hasCan: false, has4G: false, hasCamera: false, hasAccelerometer: true, category: 'GNSS' },
  'FMB962': { hasObd: true, hasBluetooth: false, hasCan: true, has4G: false, hasCamera: false, hasAccelerometer: true, category: 'GNSS' },
  'FMB964': { hasObd: true, hasBluetooth: false, hasCan: true, has4G: false, hasCamera: false, hasAccelerometer: true, category: 'GNSS' },

  // 4G OBD Series
  'FMC001': { hasObd: true, hasBluetooth: false, hasCan: false, has4G: true, hasCamera: false, hasAccelerometer: true, category: '4G OBD' },
  'FMC003': { hasObd: true, hasBluetooth: false, hasCan: false, has4G: true, hasCamera: false, hasAccelerometer: true, category: '4G OBD' },

  // 4G Professional Series
  'FMC125': { hasObd: false, hasBluetooth: true, hasCan: true, has4G: true, hasCamera: false, hasAccelerometer: true, category: '4G Pro' },
  'FMC130': { hasObd: false, hasBluetooth: false, hasCan: true, has4G: true, hasCamera: false, hasAccelerometer: true, category: '4G Pro' },
  'FMC150': { hasObd: false, hasBluetooth: false, hasCan: true, has4G: true, hasCamera: false, hasAccelerometer: true, category: '4G Pro' },
  'FMC230': { hasObd: false, hasBluetooth: false, hasCan: true, has4G: true, hasCamera: false, hasAccelerometer: true, category: '4G Pro' },
  'FMC640': { hasObd: false, hasBluetooth: false, hasCan: true, has4G: true, hasCamera: false, hasAccelerometer: true, category: '4G Pro' },
  'FMC650': { hasObd: false, hasBluetooth: false, hasCan: true, has4G: true, hasCamera: true, hasAccelerometer: true, category: '4G Pro' },
  'FMC880': { hasObd: false, hasBluetooth: false, hasCan: true, has4G: true, hasCamera: false, hasAccelerometer: true, category: '4G Pro' },

  // LTE Cat M1 Series
  'FMM001': { hasObd: true, hasBluetooth: false, hasCan: false, has4G: true, hasCamera: false, hasAccelerometer: true, category: 'Cat M1' },
  'FMM003': { hasObd: true, hasBluetooth: false, hasCan: false, has4G: true, hasCamera: false, hasAccelerometer: true, category: 'Cat M1' },
  'FMM125': { hasObd: false, hasBluetooth: false, hasCan: true, has4G: true, hasCamera: false, hasAccelerometer: true, category: 'Cat M1' },
  'FMM130': { hasObd: false, hasBluetooth: false, hasCan: true, has4G: true, hasCamera: false, hasAccelerometer: true, category: 'Cat M1' },
  'FMM230': { hasObd: false, hasBluetooth: false, hasCan: true, has4G: true, hasCamera: false, hasAccelerometer: true, category: 'Cat M1' },
  'FMM640': { hasObd: false, hasBluetooth: false, hasCan: false, has4G: true, hasCamera: false, hasAccelerometer: true, category: 'Cat M1' },

  // 3G Series
  'FM3001': { hasObd: true, hasBluetooth: false, hasCan: false, has4G: false, hasCamera: false, hasAccelerometer: true, category: '3G OBD' },
  'FM3612': { hasObd: false, hasBluetooth: true, hasCan: false, has4G: false, hasCamera: false, hasAccelerometer: true, category: '3G' },
  'FM6320': { hasObd: false, hasBluetooth: true, hasCan: true, has4G: false, hasCamera: false, hasAccelerometer: true, category: '3G' },

  // Asset Trackers - Basic GPS only
  'TAT100': { hasObd: false, hasBluetooth: false, hasCan: false, has4G: false, hasCamera: false, hasAccelerometer: true, category: 'Asset' },
  'TAT140': { hasObd: false, hasBluetooth: false, hasCan: false, has4G: true, hasCamera: false, hasAccelerometer: true, category: 'Asset' },
  'TAT141': { hasObd: false, hasBluetooth: false, hasCan: false, has4G: true, hasCamera: false, hasAccelerometer: true, category: 'Asset' },

  // Personal Trackers - Basic GPS only
  'TMT250': { hasObd: false, hasBluetooth: false, hasCan: false, has4G: false, hasCamera: false, hasAccelerometer: true, category: 'Personal' },
  'GH5200': { hasObd: false, hasBluetooth: false, hasCan: false, has4G: false, hasCamera: false, hasAccelerometer: true, category: 'Personal' },

  // Default/Other - Assume basic capabilities
  'OTHER': { hasObd: false, hasBluetooth: false, hasCan: false, has4G: false, hasCamera: false, hasAccelerometer: true, category: 'Other' },
};

// Get tracker capabilities with fallback to basic
function getTrackerCapabilities(trackerModel?: string): TrackerCapabilities {
  if (!trackerModel) {
    // Default: assume basic tracker without OBD/Bluetooth
    return { hasObd: false, hasBluetooth: false, hasCan: false, has4G: false, hasCamera: false, hasAccelerometer: true, category: 'Unknown' };
  }
  return trackerCapabilities[trackerModel] || trackerCapabilities['OTHER'];
}

// Parameters that require OBD-II connection
const obdRequiredParameters = new Set([
  'engine_rpm',
  'engine_load',
  'coolant_temp',
  'throttle_position',
  'fuel_consumption',
  'fuel_level',
  'fuel_level_liters',
  'dtc_count',
  'vin',
  'odometer', // OEM odometer requires OBD
]);

// Parameters that require Bluetooth
const bluetoothRequiredParameters = new Set([
  'ble_temperature',
  'ble_humidity',
  'ble_fuel_level',
  'tpms_pressure',
  'tpms_temperature',
]);

// Filter parameters based on tracker capabilities
function filterParametersByTrackerCapabilities(
  parameters: EnabledParameter[],
  trackerModel?: string
): EnabledParameter[] {
  const capabilities = getTrackerCapabilities(trackerModel);

  return parameters.filter(param => {
    // Check OBD requirement
    if (param.requiresObd || obdRequiredParameters.has(param.code)) {
      if (!capabilities.hasObd) {
        return false;
      }
    }

    // Check Bluetooth requirement
    if (param.requiresBluetooth || bluetoothRequiredParameters.has(param.code)) {
      if (!capabilities.hasBluetooth) {
        return false;
      }
    }

    return true;
  });
}

// Mapping from parameter codes to DevicePosition field names
const parameterFieldMapping: Record<string, keyof DevicePosition> = {
  // Position parameters
  'speed': 'speed',
  'heading': 'heading',
  'latitude': 'lat',
  'longitude': 'lng',
  'altitude': 'altitude',
  'satellites': 'satellites',
  'hdop': 'hdop',
  // Vehicle state
  'ignition': 'ignition',
  'movement': 'movement',
  'odometer': 'odometer',
  'total_odometer': 'odometer',
  'trip_odometer': 'odometer',
  // Power
  'external_voltage': 'externalVoltage',
  'battery_voltage': 'batteryVoltage',
  'battery_level': 'batteryLevel',
  'battery_current': 'batteryCurrent',
  // Fuel
  'fuel_level': 'fuelLevel',
  'fuel_consumed': 'fuelConsumed',
  'fuel_consumption': 'fuelConsumption',
  'fuel_used_gps': 'fuelConsumed',
  // Engine / OBD-II
  'engine_rpm': 'engineRpm',
  'engine_load': 'engineLoad',
  'coolant_temp': 'coolantTemp',
  'throttle_position': 'throttlePosition',
  'obd_speed': 'obdSpeed',
  'odometer_obd': 'odometer',
  // Network
  'gsm_signal': 'gsmSignal',
  'cell_id': 'cellId',
  'area_code': 'areaCode',
  // Driver behavior
  'harsh_acceleration': 'harshAcceleration',
  'harsh_braking': 'harshBraking',
  'harsh_cornering': 'harshCornering',
  // Diagnostics
  'dtc_count': 'dtcCount',
  'alarm': 'alarm',
  'crash_detection': 'alarm',
  'towing': 'alarm',
  'jamming': 'alarm',
  'unplug_detection': 'alarm',
  // Driver
  'driver_id': 'driverId',
  // EV (Electric Vehicle)
  'ev_charging': 'evCharging',
  'ev_soc': 'evSoC',
  'ev_range': 'evRange',
  'ev_soh': 'evSoH',
  'ev_battery_temp': 'evBatteryTemp',
  'ev_hv_current': 'evHvCurrent',
  'ev_hv_voltage': 'evHvVoltage',
};

// Format a parameter value based on its code and data type
function formatParameterValue(code: string, value: any, unit?: string): string {
  if (value === undefined || value === null) return '-';

  // Handle boolean values
  if (typeof value === 'boolean') {
    if (code === 'ignition') return value ? 'ON' : 'OFF';
    return value ? i18n.t('common.yes') : i18n.t('common.no');
  }

  // Handle specific formatting based on parameter code
  switch (code) {
    case 'speed':
    case 'obd_speed':
      return `${Math.round(value)} km/h`;
    case 'heading':
      return `${Math.round(value)}°`;
    case 'altitude':
      return `${Math.round(value)} m`;
    // All odometers/distances are in meters, convert to km
    case 'odometer':
    case 'total_odometer':
    case 'odometer_obd':
    case 'trip_odometer':
    case 'trip_distance':
      return `${Math.round(value / 1000).toLocaleString('fr-FR')} km`;
    // Voltages are in millivolts, convert to volts
    case 'external_voltage':
    case 'battery_voltage':
      return `${(value / 1000).toFixed(1)} V`;
    // Percentages
    case 'battery_level':
    case 'fuel_level':
    case 'engine_load':
    case 'throttle_position':
    case 'gsm_signal':
      return `${Math.round(value)}`;
    // Temperature
    case 'coolant_temp':
      return `${Math.round(value)}°C`;
    // Engine
    case 'engine_rpm':
      return `${Math.round(value).toLocaleString('fr-FR')} tr/min`;
    case 'fuel_consumption':
      return `${value.toFixed(1)} L/100km`;
    // Fuel consumed in milliliters, convert to liters
    case 'fuel_consumed':
    case 'fuel_used_gps':
      return `${(value / 1000).toFixed(1)} L`;
    // Current in milliamps, convert to amps
    case 'battery_current':
      return `${(value / 1000).toFixed(2)} A`;
    // GPS quality
    case 'hdop':
      return value.toFixed(1);
    case 'satellites':
      return `${value} sat`;
    // EV parameters
    case 'ev_charging': return value ? i18n.t('map.charging') : i18n.t('common.no');
    case 'ev_soc': return `${Math.round(value)}%`;
    case 'ev_range': return `${Math.round(value)} km`;
    case 'ev_soh': return `${Math.round(value)}%`;
    case 'ev_battery_temp': return `${Math.round(value)}°C`;
    case 'ev_hv_current': return `${(value / 10).toFixed(1)} A`;
    case 'ev_hv_voltage': return `${(value / 10).toFixed(1)} V`;
    // Movement state
    case 'movement':
      return value ? i18n.t('map.vehicle_modal.moving') : i18n.t('map.vehicle_modal.stopped');
    // Diagnostics
    case 'dtc_count':
      return `${value} code(s)`;
    // Network
    case 'cell_id':
    case 'area_code':
      return String(value);
    // Driver
    case 'driver_id':
      return String(value);
    default:
      if (unit) return `${value} ${unit}`;
      return String(value);
  }
}

// Get parameter display icon
function getParameterIcon(code: string): string {
  const icons: Record<string, string> = {
    'speed': '🚗',
    'heading': '🧭',
    'altitude': '⛰️',
    'satellites': '📡',
    'ignition': '🔑',
    'movement': '🏃',
    'odometer': '📏',
    'total_odometer': '📏',
    'trip_odometer': '🛣️',
    'external_voltage': '🔋',
    'battery_voltage': '🔌',
    'battery_level': '🔋',
    'battery_current': '⚡',
    'fuel_level': '⛽',
    'fuel_consumption': '⛽',
    'fuel_consumed': '⛽',
    'fuel_used_gps': '⛽',
    'engine_rpm': '⚙️',
    'engine_load': '📊',
    'coolant_temp': '🌡️',
    'throttle_position': '🎚️',
    'obd_speed': '🚗',
    'odometer_obd': '📏',
    'gsm_signal': '📶',
    'cell_id': '📱',
    'area_code': '📱',
    'harsh_acceleration': '⚡',
    'harsh_braking': '🛑',
    'harsh_cornering': '↩️',
    'dtc_count': '⚠️',
    'alarm': '🚨',
    'crash_detection': '💥',
    'towing': '🚛',
    'jamming': '📵',
    'unplug_detection': '🔌',
    'hdop': '📍',
    'driver_id': '👤',
    'ev_charging': '🔌',
    'ev_soc': '🔋',
    'ev_range': '🛣️',
    'ev_soh': '💚',
    'ev_battery_temp': '🌡️',
    'ev_hv_current': '⚡',
    'ev_hv_voltage': '⚡',
  };
  return icons[code] || '📌';
}

// OSRM server URL for route geometry
const OSRM_URL = import.meta.env.VITE_OSRM_URL || '/osrm';

// Cache for reverse geocoding results
const addressCache = new Map<string, string>();

// Track last rendered status per device to detect visual changes
const renderedStatus = new Map<string, string>();

// Everything that changes the marker's look: status and privacy both drive the
// fill colour, so both have to be part of the redraw key.
function markerStatusKey(device: Device): string {
  return `${device.status}:${device.privacyActive === true}`;
}

// Module-level refs for camera follow.
let _followMap: MapLibreMap | null = null;
let _followDeviceId: string | null = null;
let _followUserInteracting = false;

function calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function calculateBearing(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const y = Math.sin(dLng) * Math.cos(lat2 * Math.PI / 180);
  const x = Math.cos(lat1 * Math.PI / 180) * Math.sin(lat2 * Math.PI / 180) -
    Math.sin(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.cos(dLng);
  return ((Math.atan2(y, x) * 180 / Math.PI) + 360) % 360;
}

// Store last known position + heading per device
const previousPositions = new Map<string, { lat: number; lng: number; heading: number }>();

// Version counter per device to discard stale OSRM /nearest responses
const snapVersions = new Map<string, number>();

// Active rAF animations per device
const activeAnimations = new Map<string, number>();

// v1.12 Jitter Buffer — set to false to revert to v1.11 behavior
const USE_JITTER_BUFFER = false;

// Active waypoint consumers (one per device)
const activeConsumers = new Map<string, number>(); // imei -> setTimeout ID

// Last consumed waypoint position per device (for animation source)
const lastConsumedPosition = new Map<string, { lat: number; lng: number; heading: number; timestamp: string }>();

/**
 * Camera follow — suivi de la caméra.
 * Sans durée : setCenter instantané (premier placement, téléportation).
 * Avec durée : easeTo fluide (animation en cours).
 */
function cameraFollow(deviceId: string, lng: number, lat: number, durationMs?: number): void {
  if (!_followMap || _followDeviceId !== deviceId || _followUserInteracting) return;
  if (durationMs && durationMs > 0) {
    _followMap.easeTo({ center: [lng, lat], duration: durationMs, easing: (t: number) => t });
  } else {
    _followMap.setCenter([lng, lat]);
  }
}

/**
 * OSRM /route — géométrie route entre deux points snappés.
 * @param fromBearing — heading at start point (degrees). Constrains OSRM to
 *   pick the correct direction, avoiding spurious U-turns / roundabout loops.
 * Returns null if route distance > 3× straight-line distance (sanity check).
 */
async function getRouteGeometry(
  fromLng: number, fromLat: number,
  toLng: number, toLat: number,
  fromBearing?: number,
  toBearing?: number,
): Promise<[number, number][] | null> {
  try {
    let bearingsParam = '';
    const bFrom = (fromBearing != null && isFinite(fromBearing))
      ? `${Math.round(fromBearing) % 360},45` : '';
    const bTo = (toBearing != null && isFinite(toBearing))
      ? `${Math.round(toBearing) % 360},45` : '';
    if (bFrom || bTo) {
      bearingsParam = `&bearings=${bFrom};${bTo}`;
    }
    const res = await fetch(
      `${OSRM_URL}/route/v1/driving/${fromLng},${fromLat};${toLng},${toLat}?geometries=geojson&overview=full${bearingsParam}`,
      { signal: AbortSignal.timeout(1500) }
    );
    if (!res.ok) return null;
    const data = await res.json();
    if (data.code !== 'Ok' || !data.routes?.[0]) return null;
    const coords: [number, number][] = data.routes[0].geometry.coordinates;
    if (coords.length < 2) return null;

    // Sanity check: reject routes that are absurdly longer than straight-line
    // (e.g. double roundabout loop). Compute route polyline distance.
    let routeDist = 0;
    for (let i = 1; i < coords.length; i++) {
      routeDist += calculateDistance(coords[i-1][1], coords[i-1][0], coords[i][1], coords[i][0]);
    }
    const straightDist = calculateDistance(fromLat, fromLng, toLat, toLng);
    if (straightDist > 5 && routeDist > straightDist * 3) {
      return null; // Route aberrante, fallback sur interpolation linéaire
    }

    return coords;
  } catch {
    return null;
  }
}

/**
 * Interpole une position à la distance `d` le long de la polyline.
 * Retourne [lng, lat].
 */
function interpolatePolyline(
  coords: [number, number][], cumDist: number[], totalDist: number, d: number
): [number, number] {
  let si = 0;
  for (let i = 1; i < cumDist.length; i++) {
    if (cumDist[i] >= d) { si = i - 1; break; }
    si = i - 1;
  }
  const sLen = (cumDist[si + 1] ?? totalDist) - cumDist[si];
  const st = sLen > 0 ? (d - cumDist[si]) / sLen : 0;
  const c0 = coords[si], c1 = coords[si + 1] ?? c0;
  return [
    c0[0] + (c1[0] - c0[0]) * st,
    c0[1] + (c1[1] - c0[1]) * st,
  ];
}

/**
 * Animation fluide le long d'une polyline OSRM ou en ligne droite.
 * Heading : lookahead 20m sur la polyline (pas de saut entre segments).
 * Fallback lerp linéaire si pas de polyline.
 */
function smoothMove(
  marker: Marker,
  deviceId: string,
  fromLat: number,
  fromLng: number,
  toLat: number,
  toLng: number,
  toHeading: number,
  durationMs: number,
  path?: [number, number][] | null,
): void {
  // Animation for this device is already cancelled by applyPosition before
  // the route request — no need to cancel here again.

  const fromH = marker.getRotation();

  // Construire la polyline avec distances cumulées
  let coords: [number, number][] | null = null;
  let cumDist: number[] | null = null;
  let totalDist = 0;

  if (path && path.length >= 2) {
    // La route OSRM part déjà de la position du marqueur — pas de prepend
    coords = path;
    cumDist = [0];
    for (let i = 1; i < coords.length; i++) {
      totalDist += calculateDistance(coords[i-1][1], coords[i-1][0], coords[i][1], coords[i][0]);
      cumDist.push(totalDist);
    }
  }

  let dH = toHeading - fromH;
  if (dH > 180) dH -= 360;
  if (dH < -180) dH += 360;

  const LOOKAHEAD = 20; // mètres

  const startTime = performance.now();

  function tick(now: number) {
    const t = Math.min((now - startTime) / durationMs, 1);
    let lat: number, lng: number, heading: number;

    if (coords && cumDist && totalDist > 0) {
      const d = t * totalDist;
      const pos = interpolatePolyline(coords, cumDist, totalDist, d);
      lng = pos[0];
      lat = pos[1];

      // Lookahead 20m pour heading fluide
      const dAhead = Math.min(d + LOOKAHEAD, totalDist);
      const ahead = interpolatePolyline(coords, cumDist, totalDist, dAhead);
      const lookDist = calculateDistance(lat, lng, ahead[1], ahead[0]);
      if (lookDist > 1) {
        heading = calculateBearing(lat, lng, ahead[1], ahead[0]);
      } else {
        // Fin de la polyline : utiliser le bearing du dernier segment de route
        // au lieu de sauter vers toHeading (GPS) → évite le spin du marqueur
        const c0 = coords[coords.length - 2];
        const c1 = coords[coords.length - 1];
        heading = calculateBearing(c0[1], c0[0], c1[1], c1[0]);
      }
    } else {
      lat = fromLat + (toLat - fromLat) * t;
      lng = fromLng + (toLng - fromLng) * t;
      heading = fromH + dH * t;
    }

    marker.setLngLat([lng, lat]);
    marker.setRotation(heading);

    if (t < 1) {
      activeAnimations.set(deviceId, requestAnimationFrame(tick));
    } else {
      activeAnimations.delete(deviceId);
    }
  }

  // Caméra : un seul easeTo fluide vers la destination
  cameraFollow(deviceId, toLng, toLat, durationMs);
  activeAnimations.set(deviceId, requestAnimationFrame(tick));
}

/**
 * Jitter buffer — consume waypoints one-by-one from the position queue,
 * animating the marker smoothly between each pair of consecutive waypoints.
 */
function consumeNextWaypoint(
  imei: string,
  deviceId: string,
  marker: Marker,
): void {
  const waypoint = shiftPositionQueue(imei);
  if (!waypoint) {
    // Queue empty — stop consumer
    activeConsumers.delete(imei);
    return;
  }

  const prev = lastConsumedPosition.get(imei);

  // Snap waypoint destination to road for accurate placement
  const applyWaypoint = async (finalLng: number, finalLat: number) => {
    // Calculate heading
    let bearing: number;
    if (!prev) {
      bearing = waypoint.heading ?? 0;
    } else if (waypoint.speed < 2) {
      bearing = prev.heading;
    } else {
      const d = calculateDistance(prev.lat, prev.lng, finalLat, finalLng);
      if (d < 3) {
        // Too close for reliable bearing, use GPS heading or keep previous
        const hasValidGps = waypoint.heading != null && waypoint.heading > 0;
        bearing = hasValidGps ? waypoint.heading : prev.heading;
      } else {
        // Prefer GPS heading if available and valid
        const hasValidGps = waypoint.heading != null && (waypoint.heading > 0 || (waypoint.hdop != null && waypoint.hdop < 5));
        if (hasValidGps) {
          bearing = waypoint.heading;
        } else {
          bearing = calculateBearing(prev.lat, prev.lng, finalLat, finalLng);
        }
      }
    }

    // Calculate animation duration from timestamp difference
    let durationMs = 2000;
    if (prev) {
      const prevTs = new Date(prev.timestamp).getTime();
      const curTs = new Date(waypoint.timestamp).getTime();
      durationMs = curTs - prevTs;
      // Clamp between 500ms and 5000ms
      durationMs = Math.max(500, Math.min(5000, durationMs));
    }

    // Update lastConsumedPosition and previousPositions
    lastConsumedPosition.set(imei, { lat: finalLat, lng: finalLng, heading: bearing, timestamp: waypoint.timestamp });
    previousPositions.set(deviceId, { lat: finalLat, lng: finalLng, heading: bearing });

    if (!prev) {
      // First waypoint: place immediately without animation
      marker.setLngLat([finalLng, finalLat]);
      marker.setRotation(bearing);
      cameraFollow(deviceId, finalLng, finalLat);
    } else {
      const dist = calculateDistance(prev.lat, prev.lng, finalLat, finalLng);

      if (dist > 2000 || dist < 1) {
        // Teleport — too far or no movement
        marker.setLngLat([finalLng, finalLat]);
        marker.setRotation(bearing);
        cameraFollow(deviceId, finalLng, finalLat);
      } else {
        // Cancel any running rAF animation for this device
        const existingAnim = activeAnimations.get(deviceId);
        if (existingAnim) {
          cancelAnimationFrame(existingAnim);
          activeAnimations.delete(deviceId);
        }

        // Linear interpolation (no OSRM route — waypoints are close enough)
        smoothMove(marker, deviceId, prev.lat, prev.lng, finalLat, finalLng, bearing, durationMs, null);
      }
    }

    // Schedule next waypoint consumption after animation completes
    const timeoutId = window.setTimeout(() => {
      consumeNextWaypoint(imei, deviceId, marker);
    }, durationMs);
    activeConsumers.set(imei, timeoutId);
  };

  // Snap to road for moving vehicles
  if (waypoint.speed > 3) {
    const snapBearing = prev?.heading ?? waypoint.heading ?? undefined;
    snapToRoad(waypoint.lng, waypoint.lat, snapBearing).then(snapped => {
      const pos = snapped || [waypoint.lng, waypoint.lat];
      applyWaypoint(pos[0], pos[1]);
    });
  } else {
    applyWaypoint(waypoint.lng, waypoint.lat);
  }
}

function startWaypointConsumer(imei: string, deviceId: string, marker: Marker): void {
  // If consumer already active for this IMEI, do nothing — it will pick up new waypoints naturally
  if (activeConsumers.has(imei)) return;
  consumeNextWaypoint(imei, deviceId, marker);
}

function stopWaypointConsumer(imei: string): void {
  const timeoutId = activeConsumers.get(imei);
  if (timeoutId != null) {
    clearTimeout(timeoutId);
    activeConsumers.delete(imei);
  }
}

/**
 * OSRM /nearest — snap a point to the nearest road.
 * Returns snapped [lng, lat] or null on failure.
 * @param bearing — heading in degrees (0=North). Passed to OSRM to snap to
 *   the correct side of a dual carriageway / correct roundabout direction.
 */
async function snapToRoad(
  lng: number, lat: number, bearing?: number,
): Promise<[number, number] | null> {
  try {
    let url = `${OSRM_URL}/nearest/v1/driving/${lng},${lat}?number=1`;
    if (bearing != null && isFinite(bearing)) {
      url += `&bearings=${Math.round(bearing) % 360},45`;
    }
    const res = await fetch(url, { signal: AbortSignal.timeout(1500) });
    if (!res.ok) return null;
    const data = await res.json();
    if (data.code !== 'Ok' || !data.waypoints?.[0]) return null;
    const wp = data.waypoints[0];
    // Reject if snapped point is too far from original (> 50m)
    const dist = calculateDistance(lat, lng, wp.location[1], wp.location[0]);
    if (dist > 50) return null;
    return wp.location as [number, number];
  } catch {
    return null;
  }
}

/**
 * Reverse geocode coordinates to address using Nominatim server
 */
async function reverseGeocode(lat: number, lng: number): Promise<string> {
  const cacheKey = `${lat.toFixed(5)},${lng.toFixed(5)}`;

  if (addressCache.has(cacheKey)) {
    return addressCache.get(cacheKey)!;
  }

  try {
    const result = await geocodingApi.reverse(lat, lng);
    const data = result.data;

    const address = data.address || 'Adresse inconnue';

    // Cache the result
    addressCache.set(cacheKey, address);

    return address;
  } catch (error) {
    console.warn('Reverse geocoding failed:', error);
    return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
  }
}

interface Geofence {
  id: string;
  name: string;
  type: 'CIRCLE' | 'POLYGON';
  coordinates: number[] | number[][];
  radius?: number;
  color: string;
  isActive: boolean;
}

interface MapComponentProps {
  devices?: Device[];
  geofences?: Geofence[];
  onDeviceClick?: (device: Device) => void;
  selectedDeviceId?: string | null;
  showControls?: boolean;
  center?: [number, number];
  zoom?: number;
}

/**
 * Generate map style configuration based on selected style
 */
function getMapStyle(style: MapStyle): maplibregl.StyleSpecification {
  const styles: Record<MapStyle, maplibregl.StyleSpecification> = {
    'osm': {
      version: 8,
      sources: {
        'raster-tiles': {
          type: 'raster',
          tiles: [
            'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
            'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
            'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png',
          ],
          tileSize: 256,
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        },
      },
      layers: [{ id: 'raster-layer', type: 'raster', source: 'raster-tiles', minzoom: 0, maxzoom: 19 }],
    },
    'osm-humanitarian': {
      version: 8,
      sources: {
        'raster-tiles': {
          type: 'raster',
          tiles: ['https://a.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png'],
          tileSize: 256,
          attribution: '&copy; OpenStreetMap contributors, Tiles: HOT',
        },
      },
      layers: [{ id: 'raster-layer', type: 'raster', source: 'raster-tiles', minzoom: 0, maxzoom: 19 }],
    },
    'google-streets': {
      version: 8,
      sources: {
        'raster-tiles': {
          type: 'raster',
          tiles: ['https://mt0.google.com/vt/lyrs=m&hl=fr&x={x}&y={y}&z={z}'],
          tileSize: 256,
          attribution: '&copy; Google Maps',
        },
      },
      layers: [{ id: 'raster-layer', type: 'raster', source: 'raster-tiles', minzoom: 0, maxzoom: 20 }],
    },
    'google-traffic': {
      version: 8,
      sources: {
        'raster-tiles': {
          type: 'raster',
          tiles: ['https://mt0.google.com/vt/lyrs=m,traffic&hl=fr&x={x}&y={y}&z={z}&s=Ga'],
          tileSize: 256,
          attribution: '&copy; Google Maps',
        },
      },
      layers: [{ id: 'raster-layer', type: 'raster', source: 'raster-tiles', minzoom: 0, maxzoom: 20 }],
    },
    'google-satellite': {
      version: 8,
      sources: {
        'raster-tiles': {
          type: 'raster',
          tiles: ['https://mt0.google.com/vt/lyrs=s&hl=fr&x={x}&y={y}&z={z}'],
          tileSize: 256,
          attribution: '&copy; Google Maps',
        },
      },
      layers: [{ id: 'raster-layer', type: 'raster', source: 'raster-tiles', minzoom: 0, maxzoom: 20 }],
    },
    'google-hybrid': {
      version: 8,
      sources: {
        'raster-tiles': {
          type: 'raster',
          tiles: ['https://mt0.google.com/vt/lyrs=y&hl=fr&x={x}&y={y}&z={z}'],
          tileSize: 256,
          attribution: '&copy; Google Maps',
        },
      },
      layers: [{ id: 'raster-layer', type: 'raster', source: 'raster-tiles', minzoom: 0, maxzoom: 20 }],
    },
  };

  return styles[style] || styles['osm'];
}

export default function MapComponent({
  devices = [],
  geofences = [],
  onDeviceClick,
  selectedDeviceId,
  showControls = true,
  center = [2.3522, 48.8566],
  zoom = 6,
}: MapComponentProps) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<MapLibreMap | null>(null);
  const markers = useRef<Map<string, Marker>>(new Map());
  const geofenceLayers = useRef<string[]>([]);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [showLayerPicker, setShowLayerPicker] = useState(false);
  const [currentZoom, setCurrentZoom] = useState(zoom);

  // Floating info window state
  const [infoWindowDevice, setInfoWindowDevice] = useState<Device | null>(null);
  const [infoWindowPosition, setInfoWindowPosition] = useState({ x: 100, y: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [deviceAddress, setDeviceAddress] = useState<string>('');

  // Enabled parameters from subscription
  const [enabledParameters, setEnabledParameters] = useState<EnabledParameter[]>([]);
  const [shareLinkDevice, setShareLinkDevice] = useState<Device | null>(null);
  const { t } = useTranslation();

  // Get map style from settings store
  const { mapStyle, setMapStyle } = useSettingsStore();

  // Function to fetch enabled parameters
  const fetchEnabledParameters = useCallback(async () => {
    try {
      const response = await devicesApi.getEnabledParameters();
      if (response.data?.parameters) {
        setEnabledParameters(response.data.parameters);
      }
    } catch (error) {
      console.warn('Failed to fetch enabled parameters:', error);
    }
  }, []);

  // Fetch enabled parameters on mount
  useEffect(() => {
    fetchEnabledParameters();
  }, [fetchEnabledParameters]);

  // Reload parameters when info window opens
  useEffect(() => {
    if (infoWindowDevice) {
      fetchEnabledParameters();
    }
  }, [infoWindowDevice, fetchEnabledParameters]);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: getMapStyle(mapStyle),
      center: center,
      zoom: zoom,
      attributionControl: false,
    });

    map.current.addControl(
      new maplibregl.AttributionControl({ compact: true }),
      'bottom-right'
    );

    map.current.on('load', () => {
      setMapLoaded(true);
    });

    map.current.on('zoom', () => {
      setCurrentZoom(Math.round(map.current?.getZoom() || zoom));
    });

    map.current.on('error', (e) => {
      console.error('Map error:', e);
    });

    // Resize map when container size changes (e.g. sidebar open/close)
    const ro = new ResizeObserver(() => {
      map.current?.resize();
    });
    ro.observe(mapContainer.current);

    return () => {
      ro.disconnect();
      // Clean up markers
      markers.current.forEach((marker) => marker.remove());
      markers.current.clear();

      map.current?.remove();
      map.current = null;
    };
  }, []);

  // Track if this is the initial mount (to skip redundant style setting)
  const isInitialMount = useRef(true);

  // Update map style when it changes (skip initial render since style is already set in constructor)
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    // Skip the first render - style is already set in map constructor
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    map.current.setStyle(getMapStyle(mapStyle));

    // Re-add geofences after style change
    map.current.once('style.load', () => {
      setMapLoaded(true);
    });
  }, [mapStyle, mapLoaded]);

  // Update geofences
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    // Remove existing geofence layers
    geofenceLayers.current.forEach((layerId) => {
      try {
        if (map.current?.getLayer(layerId)) {
          map.current.removeLayer(layerId);
        }
        if (map.current?.getSource(layerId)) {
          map.current.removeSource(layerId);
        }
      } catch (e) {
        console.warn('Error removing layer:', e);
      }
    });
    geofenceLayers.current = [];

    // Add new geofences
    geofences
      .filter((g) => g.isActive && g.coordinates)
      .forEach((geofence) => {
        const sourceId = `geofence-${geofence.id}`;
        const fillLayerId = `geofence-fill-${geofence.id}`;
        const lineLayerId = `geofence-line-${geofence.id}`;

        let coordinates: number[][][];

        try {
          if (geofence.type === 'CIRCLE' && geofence.radius) {
            // Create circle polygon from center point
            const centerCoords = geofence.coordinates as number[];
            if (!centerCoords || centerCoords.length < 2) return;

            const points = 64;
            const km = geofence.radius / 1000;
            const coords: number[][] = [];

            for (let i = 0; i <= points; i++) {
              const angle = (i / points) * 2 * Math.PI;
              const dx = km * Math.cos(angle);
              const dy = km * Math.sin(angle);
              const lat = centerCoords[0] + (dy / 111.32);
              const lng = centerCoords[1] + (dx / (111.32 * Math.cos((centerCoords[0] * Math.PI) / 180)));
              coords.push([lng, lat]);
            }
            coordinates = [coords];
          } else {
            // Polygon geofence
            const polyCoords = geofence.coordinates as number[][];
            if (!polyCoords || !Array.isArray(polyCoords) || polyCoords.length < 3) return;
            coordinates = [polyCoords.map(c => [c[1] || c[0], c[0] || c[1]])];
          }
        } catch (e) {
          console.warn('Invalid geofence coordinates:', geofence.id, e);
          return;
        }

        try {
          map.current!.addSource(sourceId, {
            type: 'geojson',
            data: {
              type: 'Feature',
              properties: { name: geofence.name },
              geometry: {
                type: 'Polygon',
                coordinates: coordinates,
              },
            },
          });

          map.current!.addLayer({
            id: fillLayerId,
            type: 'fill',
            source: sourceId,
            paint: {
              'fill-color': geofence.color,
              'fill-opacity': 0.2,
            },
          });

          map.current!.addLayer({
            id: lineLayerId,
            type: 'line',
            source: sourceId,
            paint: {
              'line-color': geofence.color,
              'line-width': 2,
              'line-opacity': 0.8,
            },
          });

          geofenceLayers.current.push(sourceId, fillLayerId, lineLayerId);
        } catch (err) {
          console.error('Error adding geofence:', err);
        }
      });
  }, [geofences, mapLoaded]);


  // Update device markers
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    // Solution C: Only process devices whose position actually changed.
    // consumeChangedImeis() returns the set of IMEIs that received new data
    // since the last render, then clears it atomically.
    const changed = consumeChangedImeis();

    const currentMarkers = markers.current;
    const deviceIds = new Set(devices.map((d) => d.id));

    // Build deviceId→IMEI map for jitter buffer cleanup
    const deviceIdToImei = new Map<string, string>();
    devices.forEach(d => deviceIdToImei.set(d.id, d.imei));

    // Remove markers for devices that no longer exist
    currentMarkers.forEach((marker, id) => {
      if (!deviceIds.has(id)) {
        const anim = activeAnimations.get(id);
        if (anim) { cancelAnimationFrame(anim); activeAnimations.delete(id); }
        marker.remove();
        currentMarkers.delete(id);
        renderedStatus.delete(id);
        previousPositions.delete(id);
        snapVersions.delete(id);
      }
    });

    // Clean up jitter buffer state for consumers whose IMEI is no longer in the device list
    if (USE_JITTER_BUFFER) {
      const currentImeis = new Set(devices.map(d => d.imei));
      for (const imei of activeConsumers.keys()) {
        if (!currentImeis.has(imei)) {
          stopWaypointConsumer(imei);
          lastConsumedPosition.delete(imei);
        }
      }
    }

    // Add or update markers for each device
    devices.forEach((device) => {
      if (!device.lastPosition) {
        return;
      }

      const { lat, lng, heading } = device.lastPosition;

      // Validate coordinates - must be valid numbers
      if (typeof lat !== 'number' || typeof lng !== 'number' || isNaN(lat) || isNaN(lng)) {
        console.warn(`Device ${device.name} has invalid coordinates:`, device.lastPosition);
        return;
      }

      let marker = currentMarkers.get(device.id);

      // Heading fiable : ignorer heading=0 quand le GPS est perdu (satellites=0),
      // c'est une valeur par défaut Teltonika, pas un vrai cap.
      const satellites = device.lastPosition?.satellites ?? 0;
      const hasValidGpsHeading = heading != null && (heading > 0 || satellites >= 3);
      const gpsHeading = hasValidGpsHeading ? heading : null;

      // /route avec bearing sur les 2 bouts → snap implicite + géométrie route
      // Plus de /nearest : /route snappe en interne et le bearing GPS sur la
      // destination empêche le snap sur une rue parallèle / impasse
      const placeMarker = () => {
        if (!marker) return;
        const prev = previousPositions.get(device.id);
        const speed = device.lastPosition!.speed ?? 0;

        const ver = (snapVersions.get(device.id) ?? 0) + 1;
        snapVersions.set(device.id, ver);

        const applyPosition = async (rawLng: number, rawLat: number) => {
          if (snapVersions.get(device.id) !== ver || !marker) return;

          // 1. Capture current marker state WITHOUT cancelling animation.
          //    The old animation keeps running during the OSRM request below,
          //    eliminating the ~500ms freeze that caused visible 1s stops.
          const prePos = marker.getLngLat();
          const preHeading = marker.getRotation();

          // Position finale — sera mise à jour par /route si disponible
          let finalLng = rawLng;
          let finalLat = rawLat;

          // Bearing : stratégie selon la vitesse et la qualité du signal
          let bearing: number;
          if (!prev) {
            bearing = gpsHeading ?? 0;
          } else if (speed < 2) {
            bearing = prev.heading;
          } else {
            const d = calculateDistance(prev.lat, prev.lng, rawLat, rawLng);
            if (d < 3) {
              bearing = prev.heading;
            } else if (gpsHeading != null) {
              bearing = gpsHeading;
            } else if (d > 5) {
              bearing = calculateBearing(prev.lat, prev.lng, rawLat, rawLng);
            } else {
              bearing = prev.heading;
            }
          }

          // Premier placement : direct, pas d'animation
          if (!prev) {
            const anim = activeAnimations.get(device.id);
            if (anim) { cancelAnimationFrame(anim); activeAnimations.delete(device.id); }
            previousPositions.set(device.id, { lat: finalLat, lng: finalLng, heading: bearing });
            marker.setLngLat([finalLng, finalLat]);
            marker.setRotation(bearing);
            cameraFollow(device.id, finalLng, finalLat);
            return;
          }

          const dist = calculateDistance(prePos.lat, prePos.lng, rawLat, rawLng);
          // 15 km : couvre 300s @ 130 km/h (paramètres tracker max)
          if (dist > 15000 || dist < 1) {
            const anim = activeAnimations.get(device.id);
            if (anim) { cancelAnimationFrame(anim); activeAnimations.delete(device.id); }
            previousPositions.set(device.id, { lat: finalLat, lng: finalLng, heading: bearing });
            marker.setLngLat([finalLng, finalLat]);
            marker.setRotation(bearing);
            cameraFollow(device.id, finalLng, finalLat);
            return;
          }

          // --- Batch waypoint animation ---
          // If the position queue has waypoints from a batch, drain them and build
          // a polyline for smooth animation instead of jumping to the final position.
          const queuedWaypoints = getPositionQueue(device.imei);
          if (queuedWaypoints.length >= 2) {
            // Build polyline: current marker position → all waypoints → final destination
            const waypointPath: [number, number][] = [
              [prePos.lng, prePos.lat],
              ...queuedWaypoints.map(w => [w.lng, w.lat] as [number, number]),
              [rawLng, rawLat],
            ];
            clearPositionQueue(device.imei);

            // Duration based on real timestamps, compressed for animation (2-10s)
            const firstTs = new Date(queuedWaypoints[0].timestamp).getTime();
            const lastTs = new Date(queuedWaypoints[queuedWaypoints.length - 1].timestamp).getTime();
            const realSpan = lastTs - firstTs;
            const animDuration = Math.max(2000, Math.min(10000, realSpan / 10));

            // Use heading from last waypoint
            const lastWp = queuedWaypoints[queuedWaypoints.length - 1];
            bearing = lastWp.heading ?? bearing;
            finalLng = rawLng;
            finalLat = rawLat;

            // Cancel existing animation and start new one along waypoint path
            const existingAnim = activeAnimations.get(device.id);
            if (existingAnim) {
              cancelAnimationFrame(existingAnim);
              activeAnimations.delete(device.id);
            }
            previousPositions.set(device.id, { lat: finalLat, lng: finalLng, heading: bearing });
            smoothMove(marker, device.id, prePos.lat, prePos.lng, finalLat, finalLng, bearing, animDuration, waypointPath);
            return; // Skip OSRM — waypoints themselves trace the GPS path
          }

          // Durée d'animation = temps réel estimé
          let duration = 2000;
          if (speed > 1) {
            duration = Math.min(Math.max((dist / (speed / 3.6)) * 1000, 1500), 5000);
          }

          // 2. Route OSRM — l'animation précédente CONTINUE pendant la requête.
          //    bearing sur les 2 bouts : départ (direction du marqueur) +
          //    destination (heading GPS) → /route snappe les 2 extrémités
          //    SAUF si vitesse ≤ 3 km/h : GPS brut plus précis que le snap OSRM
          let path: [number, number][] | null = null;
          if (dist > 10 && speed > 3) {
            path = await getRouteGeometry(
              prePos.lng, prePos.lat, rawLng, rawLat,
              preHeading,
              gpsHeading ?? undefined,
            );
            if (snapVersions.get(device.id) !== ver || !marker) return;

            // Utiliser la destination snappée par /route (dernier point de la géométrie)
            if (path && path.length >= 2) {
              finalLng = path[path.length - 1][0];
              finalLat = path[path.length - 1][1];
            }
          }

          // 3. NOW cancel the old animation and capture the marker's actual position
          const existingAnim = activeAnimations.get(device.id);
          if (existingAnim) {
            cancelAnimationFrame(existingAnim);
            activeAnimations.delete(device.id);
          }
          const frozen = marker.getLngLat();

          // Stocker la position snappée (destination /route ou GPS brut si pas de route)
          previousPositions.set(device.id, { lat: finalLat, lng: finalLng, heading: bearing });

          // 4. Animation depuis la position actuelle du marqueur
          smoothMove(marker, device.id, frozen.lat, frozen.lng, finalLat, finalLng, bearing, duration, path);
        };

        // GPS brut vers applyPosition :
        // - En mouvement (speed > 3) → /route snappe via bearing sur les 2 bouts
        // - À l'arrêt (speed ≤ 3) → GPS brut direct, pas de snap OSRM
        applyPosition(lng, lat);
      };

      if (marker) {
        // Update marker appearance if status changed (ONLINE↔OFFLINE or privacy on/off)
        const prevRenderedStatus = renderedStatus.get(device.id);
        const currentStatus = markerStatusKey(device);
        if (prevRenderedStatus && prevRenderedStatus !== currentStatus) {
          const newEl = createMarkerElement(device);
          const oldEl = marker.getElement();
          oldEl.innerHTML = newEl.innerHTML;
          patchMarkerPlateLabel(marker);
          renderedStatus.set(device.id, currentStatus);
        }

        // Skip position processing for devices that haven't changed
        if (!changed.has(device.imei)) {
          if (infoWindowDevice?.id === device.id) {
            setInfoWindowDevice(device);
          }
          return;
        }

        if (USE_JITTER_BUFFER && getPositionQueueLength(device.imei) > 0) {
          // v1.12: Consume waypoints from the position queue via jitter buffer
          startWaypointConsumer(device.imei, device.id, marker);
        } else {
          // v1.11 behavior: direct animation (also used when queue is empty,
          // e.g. single position:update events without batching)
          placeMarker();
        }

        // Update floating info window if this device is selected
        if (infoWindowDevice?.id === device.id) {
          setInfoWindowDevice(device);
          if (device.lastPosition) {
            const newLat = device.lastPosition.lat;
            const newLng = device.lastPosition.lng;
            const prev = previousPositions.get(device.id);
            if (prev) {
              const distance = calculateDistance(prev.lat, prev.lng, newLat, newLng);
              if (distance > 50) {
                reverseGeocode(newLat, newLng).then(setDeviceAddress);
              }
            }
          }
        }
      } else {
        // Create new marker
        const el = createMarkerElement(device);

        // For jitter buffer: use first queued waypoint as initial position if available
        let initialLat = lat;
        let initialLng = lng;
        let initialHeading = gpsHeading ?? 0;
        if (USE_JITTER_BUFFER) {
          const firstWp = peekPositionQueue(device.imei);
          if (firstWp) {
            initialLat = firstWp.lat;
            initialLng = firstWp.lng;
            initialHeading = firstWp.heading ?? 0;
          }
        }

        marker = new Marker({
          element: el,
          rotation: initialHeading,
          anchor: 'center',
        })
          .setLngLat([initialLng, initialLat])
          .addTo(map.current!);
        patchMarkerPlateLabel(marker);

        previousPositions.set(device.id, { lat: initialLat, lng: initialLng, heading: initialHeading });

        if (USE_JITTER_BUFFER) {
          // Seed lastConsumedPosition so the consumer has a starting point
          lastConsumedPosition.set(device.imei, {
            lat: initialLat, lng: initialLng,
            heading: initialHeading,
            timestamp: device.lastPosition!.timestamp,
          });
          // Start consuming queued waypoints
          startWaypointConsumer(device.imei, device.id, marker);
        }

        // Handler for both click and touch events
        const handleMarkerInteraction = (clientX: number, clientY: number) => {
          // Prevent auto-fit from interfering
          hasInitialFit.current = true;

          // Show floating info window
          const rect = mapContainer.current?.getBoundingClientRect();
          if (rect) {
            const mobile = window.matchMedia('(max-width: 767px)').matches;
            if (mobile) {
              // Center the modal on screen
              const modalWidth = 320;
              const modalHeight = 300;
              setInfoWindowPosition({
                x: Math.max(8, (rect.width - modalWidth) / 2),
                y: Math.max(8, (rect.height - modalHeight) / 2),
              });
            } else {
              setInfoWindowPosition({
                x: Math.min(clientX - rect.left + 20, rect.width - 340),
                y: Math.max(clientY - rect.top - 100, 20),
              });
            }
          }
          setInfoWindowDevice(device);
          // Load address for the device
          if (device.lastPosition) {
            setDeviceAddress(''); // Reset address while loading
            reverseGeocode(device.lastPosition.lat, device.lastPosition.lng).then(setDeviceAddress);
            // Zoom to vehicle
            map.current?.flyTo({
              center: [device.lastPosition.lng, device.lastPosition.lat],
              zoom: 18,
              duration: 1000,
            });
          }
          onDeviceClick?.(device);
        };

        // Mouse click event
        el.addEventListener('click', (e) => {
          e.stopPropagation();
          handleMarkerInteraction(e.clientX, e.clientY);
        });

        // Touch event for mobile
        el.addEventListener('touchend', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const touch = e.changedTouches[0];
          if (touch) {
            handleMarkerInteraction(touch.clientX, touch.clientY);
          }
        });

        currentMarkers.set(device.id, marker);
        renderedStatus.set(device.id, markerStatusKey(device));
      }
    });
  }, [devices, onDeviceClick, mapLoaded]);

  // Track which device we've already centered on
  const lastCenteredDeviceId = useRef<string | null>(null);

  // Track if user is manually interacting with the map (to pause auto-follow)
  const userInteracting = useRef(false);
  const autoFollowTimeout = useRef<number | null>(null);

  // Setup map interaction listeners for auto-follow pause
  useEffect(() => {
    if (!map.current) return;

    const onInteractionStart = () => {
      userInteracting.current = true;
      _followUserInteracting = true;
      if (autoFollowTimeout.current) {
        clearTimeout(autoFollowTimeout.current);
      }
    };

    const onInteractionEnd = () => {
      // Resume auto-follow after 5 seconds of no interaction
      autoFollowTimeout.current = window.setTimeout(() => {
        userInteracting.current = false;
        _followUserInteracting = false;
      }, 5000);
    };

    map.current.on('dragstart', onInteractionStart);
    map.current.on('zoomstart', onInteractionStart);
    map.current.on('dragend', onInteractionEnd);
    map.current.on('zoomend', onInteractionEnd);

    return () => {
      map.current?.off('dragstart', onInteractionStart);
      map.current?.off('zoomstart', onInteractionStart);
      map.current?.off('dragend', onInteractionEnd);
      map.current?.off('zoomend', onInteractionEnd);
      if (autoFollowTimeout.current) {
        clearTimeout(autoFollowTimeout.current);
      }
    };
  }, [mapLoaded]);

  // Center on selected device (only when selection changes, not on every position update)
  useEffect(() => {
    if (!map.current || !selectedDeviceId) {
      lastCenteredDeviceId.current = null;
      return;
    }

    // Only fly to device if it's a new selection
    if (lastCenteredDeviceId.current === selectedDeviceId) {
      return;
    }

    const device = devices.find((d) => d.id === selectedDeviceId);
    if (device?.lastPosition) {
      const { lat, lng } = device.lastPosition;
      if (typeof lat === 'number' && typeof lng === 'number' && !isNaN(lat) && !isNaN(lng)) {
        lastCenteredDeviceId.current = selectedDeviceId;
        userInteracting.current = false;
        map.current.flyTo({
          center: [lng, lat],
          zoom: 18,
          duration: 1000,
        });

        // Show floating info window
        setInfoWindowDevice(device);
        // Center modal on mobile, fixed position on desktop
        const mobile = window.matchMedia('(max-width: 767px)').matches;
        const rect = mapContainer.current?.getBoundingClientRect();
        if (mobile && rect) {
          const modalWidth = 320;
          const modalHeight = 300;
          setInfoWindowPosition({
            x: Math.max(8, (rect.width - modalWidth) / 2),
            y: Math.max(8, (rect.height - modalHeight) / 2),
          });
        } else {
          setInfoWindowPosition({ x: 100, y: 100 });
        }
        reverseGeocode(lat, lng).then(setDeviceAddress);
      }
    }
  }, [selectedDeviceId, devices]);

  // Sync module-level refs so cameraFollow() can track the followed device.
  // Camera follows frame-by-frame via map.setCenter() inside each rAF loop.
  useEffect(() => {
    _followMap = map.current ?? null;
    _followDeviceId = infoWindowDevice?.id ?? null;
  }, [infoWindowDevice, mapLoaded]);

  const handleZoomIn = () => map.current?.zoomIn();
  const handleZoomOut = () => map.current?.zoomOut();

  const handleLocate = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          map.current?.flyTo({
            center: [position.coords.longitude, position.coords.latitude],
            zoom: 14,
          });
        },
        (error) => console.error('Geolocation error:', error)
      );
    }
  };

  const handleFitBounds = useCallback(() => {
    if (!map.current || devices.length === 0) return;

    const bounds = new maplibregl.LngLatBounds();
    let hasPositions = false;

    devices.forEach((device) => {
      if (device.lastPosition) {
        const { lat, lng } = device.lastPosition;
        if (typeof lat === 'number' && typeof lng === 'number' && !isNaN(lat) && !isNaN(lng)) {
          bounds.extend([lng, lat]);
          hasPositions = true;
        }
      }
    });

    if (hasPositions) {
      map.current.fitBounds(bounds, { padding: 50, maxZoom: 15 });
    }
  }, [devices]);

  // Auto-fit bounds on initial load when devices are available
  const hasInitialFit = useRef(false);
  useEffect(() => {
    // Don't auto-fit if a device is already selected (user clicked on something)
    if (!mapLoaded || hasInitialFit.current || devices.length === 0 || infoWindowDevice) return;

    // Check if at least one device has a position
    const hasDevicesWithPosition = devices.some(
      d => d.lastPosition &&
           typeof d.lastPosition.lat === 'number' &&
           typeof d.lastPosition.lng === 'number'
    );

    if (hasDevicesWithPosition) {
      // Small delay to ensure markers are rendered
      setTimeout(() => {
        // Double-check no device was selected during the delay
        if (!infoWindowDevice) {
          handleFitBounds();
        }
        hasInitialFit.current = true;
      }, 300);
    }
  }, [mapLoaded, devices, handleFitBounds, infoWindowDevice]);

  // Drag handlers for floating info window (mouse)
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragOffset({
      x: e.clientX - infoWindowPosition.x,
      y: e.clientY - infoWindowPosition.y,
    });
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (isDragging) {
      setInfoWindowPosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y,
      });
    }
  }, [isDragging, dragOffset]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Touch handlers for mobile/tablet dragging
  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      setIsDragging(true);
      setDragOffset({
        x: touch.clientX - infoWindowPosition.x,
        y: touch.clientY - infoWindowPosition.y,
      });
    }
  };

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (isDragging && e.touches.length === 1) {
      e.preventDefault(); // Prevent scrolling while dragging
      const touch = e.touches[0];
      setInfoWindowPosition({
        x: touch.clientX - dragOffset.x,
        y: touch.clientY - dragOffset.y,
      });
    }
  }, [isDragging, dragOffset]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add/remove global event listeners for dragging (mouse + touch)
  useEffect(() => {
    if (isDragging) {
      // Mouse events
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      // Touch events
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('touchend', handleTouchEnd);
      window.addEventListener('touchcancel', handleTouchEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  // Format time ago for info window
  const formatTimeAgo = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return t('map.time_ago.now');
    if (diffMins < 60) return t('map.time_ago.minutes', { count: diffMins });
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return t('map.time_ago.hours', { count: diffHours });
    const diffDays = Math.floor(diffHours / 24);
    return t('map.time_ago.days', { count: diffDays });
  };

  return (
    <div className="relative w-full h-full bg-gray-100">
      <div ref={mapContainer} className="w-full h-full" />

      {showControls && (
        <div className="absolute bottom-[100px] md:bottom-4 left-4 flex flex-col gap-2 z-10">
          <button
            onClick={handleZoomIn}
            className="p-2.5 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors shadow-md"
            title={t('map.controls.zoom_in')}
          >
            <ZoomIn className="h-5 w-5 text-gray-700" />
          </button>
          <button
            onClick={handleZoomOut}
            className="p-2.5 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors shadow-md"
            title={t('map.controls.zoom_out')}
          >
            <ZoomOut className="h-5 w-5 text-gray-700" />
          </button>
          <button
            onClick={handleLocate}
            className="p-2.5 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors shadow-md"
            title={t('map.controls.my_location')}
          >
            <Locate className="h-5 w-5 text-gray-700" />
          </button>
          <button
            onClick={handleFitBounds}
            className="p-2.5 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors shadow-md"
            title={t('map.controls.fit_all')}
          >
            <Maximize2 className="h-5 w-5 text-gray-700" />
          </button>
          <button
            onClick={() => setShowLayerPicker(!showLayerPicker)}
            className={`p-2.5 rounded-lg border transition-colors shadow-md ${
              showLayerPicker
                ? 'bg-green-500 border-green-500 text-white'
                : 'bg-white border-gray-200 hover:bg-gray-50 text-gray-700'
            }`}
            title="Changer le fond de carte"
          >
            <Layers className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* Zoom level indicator */}
      {showControls && (
        <div className="absolute bottom-4 right-4 z-50 bg-white px-3 py-2 rounded-lg border border-gray-300 shadow-lg hidden md:block">
          <span className="text-sm font-bold text-gray-900">Zoom: {currentZoom}</span>
        </div>
      )}

      {/* Layer picker panel */}
      {showLayerPicker && (
        <div className="absolute bottom-[100px] md:bottom-4 left-20 z-20 bg-white rounded-lg border border-gray-200 shadow-lg p-3 w-64">
          <div className="text-sm font-medium text-gray-900 mb-2">Fond de carte</div>
          <div className="space-y-1">
            {MAP_STYLES.map((style) => (
              <button
                key={style.id}
                onClick={() => {
                  setMapStyle(style.id);
                  setShowLayerPicker(false);
                }}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                  mapStyle === style.id
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'hover:bg-gray-50 text-gray-700'
                }`}
              >
                <div className="font-medium">{style.name}</div>
                <div className="text-xs text-gray-500">{style.description}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Floating Info Window */}
      {infoWindowDevice && (
        <div
          className="absolute z-30 bg-white rounded-xl shadow-2xl border border-gray-200 w-[320px] sm:w-[360px] max-w-[calc(100vw-32px)]"
          style={{
            left: infoWindowPosition.x,
            top: infoWindowPosition.y,
            cursor: isDragging ? 'grabbing' : 'default',
            touchAction: 'none', // Prevent browser touch behaviors on the modal
          }}
        >
          {/* Header - Draggable (mouse + touch) */}
          <div
            className="flex items-center justify-between p-3 bg-gray-50 rounded-t-xl border-b border-gray-200 cursor-grab active:cursor-grabbing select-none"
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
          >
            <div className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: infoWindowDevice.privacyActive ? '#6366f1' : infoWindowDevice.status === 'ONLINE' ? '#22c55e' : '#94a3b8' }}
              />
              <span className="font-semibold text-gray-900">{infoWindowDevice.name}</span>
              {infoWindowDevice.plate && (
                <span className="text-xs bg-gray-200 px-2 py-0.5 rounded text-gray-600">
                  {infoWindowDevice.plate}
                </span>
              )}
            </div>
            <button
              onClick={() => setInfoWindowDevice(null)}
              className="p-1 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="p-4 space-y-3">
            {/* Status badges */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Privacy / Online / Offline — privacy first, it explains the silence */}
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                infoWindowDevice.privacyActive
                  ? 'bg-indigo-100 text-indigo-700'
                  : infoWindowDevice.status === 'ONLINE'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-600'
              }`}>
                {infoWindowDevice.privacyActive
                  ? 'Confidentialité'
                  : infoWindowDevice.status === 'ONLINE'
                    ? 'En ligne'
                    : 'Hors ligne'}
              </span>
              {/* Tracker model badge */}
              {infoWindowDevice.trackerModel && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                  {infoWindowDevice.trackerModel}
                </span>
              )}
              {/* Ignition status */}
              {infoWindowDevice.lastPosition?.ignition !== undefined && (
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  infoWindowDevice.lastPosition.ignition
                    ? 'bg-orange-100 text-orange-700'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {infoWindowDevice.lastPosition.ignition ? 'Contact ON' : 'Contact OFF'}
                </span>
              )}
              {/* Movement status */}
              {infoWindowDevice.lastPosition && infoWindowDevice.lastPosition.speed >= 5 && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                  {t('map.vehicle_modal.moving')}
                </span>
              )}
              {infoWindowDevice.lastPosition && infoWindowDevice.lastPosition.speed < 5 && infoWindowDevice.status === 'ONLINE' && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                  {t('map.vehicle_modal.stopped')}
                </span>
              )}
            </div>

            {/* Tracker capabilities (if tracker model is known) */}
            {infoWindowDevice.trackerModel && (() => {
              const caps = getTrackerCapabilities(infoWindowDevice.trackerModel);
              const badges = [];
              if (caps.hasObd) badges.push({ label: 'OBD', color: 'bg-blue-100 text-blue-600' });
              if (caps.hasBluetooth) badges.push({ label: 'BT', color: 'bg-indigo-100 text-indigo-600' });
              if (caps.hasCan) badges.push({ label: 'CAN', color: 'bg-cyan-100 text-cyan-600' });
              if (caps.has4G) badges.push({ label: '4G', color: 'bg-green-100 text-green-600' });
              if (caps.hasCamera) badges.push({ label: 'CAM', color: 'bg-pink-100 text-pink-600' });

              if (badges.length === 0) return null;

              return (
                <div className="flex flex-wrap gap-1 mt-1">
                  {badges.map(b => (
                    <span key={b.label} className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${b.color}`}>
                      {b.label}
                    </span>
                  ))}
                </div>
              );
            })()}

            {/* Position info */}
            {infoWindowDevice.lastPosition && (
              <>
                {/* Address */}
                {deviceAddress && (
                  <div className="flex items-start gap-2">
                    <svg className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className="text-sm text-gray-600">{deviceAddress}</span>
                  </div>
                )}

                {/* Speed & Heading (always shown as primary data) */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-gray-50 rounded-lg p-2 text-center">
                    <div className="text-xs text-gray-500">{t('map.vehicle_modal.speed')}</div>
                    <div className="font-semibold text-gray-900">
                      {Math.round(infoWindowDevice.lastPosition.speed)} km/h
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-2 text-center">
                    <div className="text-xs text-gray-500">{t('map.vehicle_modal.heading')}</div>
                    <div className="font-semibold text-gray-900">
                      {infoWindowDevice.lastPosition.heading ? `${Math.round(infoWindowDevice.lastPosition.heading)}°` : '-'}
                    </div>
                  </div>
                </div>

                {/* Dynamic parameters based on subscription - compact grid without category headers */}
                {enabledParameters.length > 0 && (() => {
                  const pos = infoWindowDevice.lastPosition;
                  // Filter parameters based on tracker capabilities
                  const trackerModel = infoWindowDevice.trackerModel;
                  const capabilityFilteredParams = filterParametersByTrackerCapabilities(
                    enabledParameters,
                    trackerModel
                  );

                  // Filter parameters that have data and exclude speed/heading (already shown above)
                  const availableParams = capabilityFilteredParams.filter(param => {
                    if (param.code === 'speed' || param.code === 'heading') return false;
                    const fieldName = parameterFieldMapping[param.code];
                    if (!fieldName) return false;
                    const value = pos[fieldName];
                    return value !== undefined && value !== null;
                  });

                  if (availableParams.length === 0) return null;

                  return (
                    <div className="grid grid-cols-2 gap-2">
                      {availableParams.map(param => {
                        const fieldName = parameterFieldMapping[param.code];
                        const value = pos[fieldName];
                        return (
                          <div key={param.id} className="bg-gray-50 rounded-lg p-2 text-center">
                            <div className="flex items-center justify-center gap-1.5 text-xs text-gray-500 mb-0.5">
                              <span className="text-sm">{getParameterIcon(param.code)}</span>
                              <span>{t(`tracker_params.${param.code}`, { defaultValue: param.name })}</span>
                            </div>
                            <div className="font-semibold text-gray-900">
                              {formatParameterValue(param.code, value, param.unit)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}

                {/* Fallback: Show basic data if no enabled parameters are configured */}
                {enabledParameters.length === 0 && (
                  <div className="grid grid-cols-2 gap-2">
                    {infoWindowDevice.lastPosition.odometer && (
                      <div className="bg-gray-50 rounded-lg p-2 text-center">
                        <div className="text-xs text-gray-500">{t('map.vehicle_modal.odometer')}</div>
                        <div className="font-semibold text-gray-900">
                          {Math.round(infoWindowDevice.lastPosition.odometer / 1000).toLocaleString()} km
                        </div>
                      </div>
                    )}
                    {infoWindowDevice.lastPosition.externalVoltage && (
                      <div className="bg-gray-50 rounded-lg p-2 text-center">
                        <div className="text-xs text-gray-500">{t('map.vehicle_modal.vehicle_battery')}</div>
                        <div className="font-semibold text-gray-900">
                          {(infoWindowDevice.lastPosition.externalVoltage / 1000).toFixed(1)} V
                        </div>
                      </div>
                    )}
                    {infoWindowDevice.lastPosition.batteryVoltage && (
                      <div className="bg-gray-50 rounded-lg p-2 text-center">
                        <div className="text-xs text-gray-500">{t('map.vehicle_modal.gps_battery')}</div>
                        <div className="font-semibold text-gray-900">
                          {(infoWindowDevice.lastPosition.batteryVoltage / 1000).toFixed(2)} V
                        </div>
                      </div>
                    )}
                    {infoWindowDevice.lastPosition.satellites !== undefined && (
                      <div className="bg-gray-50 rounded-lg p-2 text-center">
                        <div className="text-xs text-gray-500">{t('map.vehicle_modal.satellites')}</div>
                        <div className="font-semibold text-gray-900">
                          {infoWindowDevice.lastPosition.satellites}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* OEM Connect — manufacturer data (locks, openings, tyres…) */}
                {infoWindowDevice.lastPosition.oem && (
                  <OemDataPanel
                    oem={infoWindowDevice.lastPosition.oem}
                    deviceId={infoWindowDevice.id}
                  />
                )}

                {/* Last update */}
                <div className="text-xs text-gray-500 pt-2 border-t border-gray-100">
                  {t('map.vehicle_modal.last_update', { time: formatTimeAgo(infoWindowDevice.lastPosition.timestamp) })}
                </div>
              </>
            )}

            {/* Actions */}
            <div className="pt-2">
              <button
                onClick={() => setShareLinkDevice(infoWindowDevice)}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-cyan-50 text-cyan-700 hover:bg-cyan-100 border border-cyan-200 rounded-lg text-sm font-medium transition-colors"
              >
                <Share2 className="w-4 h-4" />
                {t('map.vehicle_modal.send_tracking_link')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share-link modal (vehicle-only mode, max 1h) */}
      <ShareLinkModal
        open={shareLinkDevice !== null}
        onClose={() => setShareLinkDevice(null)}
        deviceId={shareLinkDevice?.id ?? ''}
        deviceName={shareLinkDevice?.name ?? ''}
      />


      {/* Custom CSS */}
      <style>{`
        .maplibregl-ctrl-attrib {
          background: rgba(255, 255, 255, 0.9) !important;
          color: #64748b !important;
          font-size: 10px !important;
        }
        .maplibregl-ctrl-attrib a {
          color: #475569 !important;
        }
        .device-marker {
          cursor: pointer;
        }
        .device-marker:hover {
          filter: brightness(1.1);
        }
        @keyframes marker-ping {
          75%, 100% {
            transform: scale(2);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
}

/**
 * Patch marker.setRotation so the plate label always stays horizontal
 * AND always stays visually above the badge, regardless of heading.
 *
 * The marker element rotates by θ. To keep the label at a fixed screen
 * offset (0, -offset) from the badge center, in the rotated local frame
 * the label must be at (-offset·sinθ, -offset·cosθ) with rotation -θ.
 */
function patchMarkerPlateLabel(marker: Marker): void {
  const el = marker.getElement();
  const label = el.querySelector('.plate-label') as HTMLElement | null;
  if (!label) return;
  const OFFSET = 25; // px above badge center
  const applyCounterRotation = (deg: number) => {
    const rad = deg * Math.PI / 180;
    const dx = -OFFSET * Math.sin(rad);
    const dy = -OFFSET * Math.cos(rad);
    label.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) rotate(${-deg}deg)`;
  };
  const origSetRotation = marker.setRotation.bind(marker);
  marker.setRotation = (rotation: number) => {
    applyCounterRotation(rotation);
    return origSetRotation(rotation);
  };
  // Apply initial counter-rotation
  const initial = (marker as any)._rotation ?? 0;
  applyCounterRotation(initial);
}

// --- OEM Connect info panel ------------------------------------------------
// Renders the rich manufacturer status (locks, openings, tyres, fluids, maintenance)
// pulled from the OEM cloud. Keys/values are localized here so the provider stays
// language-agnostic; unknown keys fall back to a prettified label.

const OEM_FIELD_LABELS: Record<string, string> = {
  trunk_lock: 'Coffre',
  hood_lock: 'Capot',
  parking_brake: 'Frein à main',
  sunroof: 'Toit ouvrant',
  windows_open: 'Vitres ouvertes',
  estimated_range_km: 'Autonomie',
  engine_oil_pct: "Niveau d'huile",
  aux_battery: 'Batterie 12V',
  next_service_km: 'Prochaine révision',
  next_service_days: 'Révision dans',
  next_oil_service_km: 'Prochaine vidange',
  next_oil_service_days: 'Vidange dans',
  active_warnings: 'Voyants actifs',
};

const OEM_LOCATION_LABELS: Record<string, string> = {
  front_left: 'AV gauche',
  front_right: 'AV droite',
  rear_left: 'AR gauche',
  rear_right: 'AR droite',
  rear_left_outer: 'AR gauche ext.',
  rear_right_outer: 'AR droite ext.',
  spare: 'secours',
  hatch: 'hayon',
};

const OEM_VALUE_LABELS: Record<string, string> = {
  locked: 'Verrouillé',
  unlocked: 'Déverrouillé',
  open: 'Ouvert',
  closed: 'Fermé',
  intermediate: 'Entrouvert',
  active: 'Actif',
  inactive: 'Inactif',
  ok: 'OK',
  low: 'Faible',
};

function oemFieldLabel(key: string): string {
  if (OEM_FIELD_LABELS[key]) return OEM_FIELD_LABELS[key];
  const tyre = key.match(/^tire_pressure_(.+)_bar$/);
  if (tyre) return `Pression ${OEM_LOCATION_LABELS[tyre[1]] ?? tyre[1].replace(/_/g, ' ')}`;
  return key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

function oemFieldValue(key: string, value: string | number | boolean): string {
  if (typeof value === 'boolean') {
    if (key === 'parking_brake') return value ? 'Serré' : 'Desserré';
    return value ? 'Oui' : 'Non';
  }
  if (typeof value === 'number') {
    if (key.endsWith('_km')) return `${value.toLocaleString('fr-FR')} km`;
    if (key.endsWith('_days')) return `${value} j`;
    if (key.endsWith('_pct')) return `${value} %`;
    if (key.endsWith('_bar')) return `${value.toFixed(1)} bar`;
    return String(value);
  }
  return OEM_VALUE_LABELS[value] ?? value;
}

// Remote lock/unlock is gated on the manufacturer/provider granting the commands
// capability (High Mobility currently exposes read-only `vehicle:data` only).
// Flip to true once that is enabled — the backend route + RBAC are already wired.
const OEM_REMOTE_CONTROL_ENABLED: boolean = false;

// Roles allowed to send remote commands — mirrors the tracker-command gate
// (VIEWER excluded). Enforced server-side; this only hides the UI affordance.
const OEM_CONTROL_ROLES = ['SUPER_ADMIN', 'ADMIN', 'MANAGER', 'OPERATOR'];

function OemDataPanel({ oem, deviceId }: { oem: OemVehicleState; deviceId: string }) {
  const role = useAuthStore((s) => s.user?.role) ?? '';
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const entries = Object.entries(oem.fields ?? {});
  if (!oem.centralLock && entries.length === 0) return null;

  const canControl = OEM_CONTROL_ROLES.includes(role);
  const lock = oem.centralLock;
  const lockStyle =
    lock === 'locked'
      ? 'bg-green-100 text-green-700'
      : lock === 'unlocked'
        ? 'bg-red-100 text-red-700'
        : 'bg-amber-100 text-amber-700';
  const lockLabel =
    lock === 'locked' ? 'Verrouillé' : lock === 'unlocked' ? 'Déverrouillé' : 'Partiel';

  const runCommand = async (locked: boolean) => {
    setBusy(true);
    setMsg(null);
    try {
      await (locked ? devicesApi.lockOem(deviceId) : devicesApi.unlockOem(deviceId));
      setMsg(locked ? 'Commande de verrouillage envoyée.' : 'Commande de déverrouillage envoyée.');
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      setMsg(e.response?.data?.error ?? 'Échec de la commande.');
    } finally {
      setBusy(false);
    }
  };

  const controlTooltip = OEM_REMOTE_CONTROL_ENABLED
    ? undefined
    : 'Commandes à distance bientôt disponibles (activation constructeur requise).';

  return (
    <div className="pt-2 border-t border-gray-100">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
          Données constructeur
        </span>
        {lock && (
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${lockStyle}`}>
            {lock === 'locked' ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
            {lockLabel}
          </span>
        )}
      </div>
      {entries.length > 0 && (
        <div className="grid grid-cols-2 gap-2 max-h-56 overflow-y-auto">
          {entries.map(([key, value]) => (
            <div key={key} className="bg-gray-50 rounded-lg p-2 text-center">
              <div className="text-xs text-gray-500 mb-0.5 truncate" title={oemFieldLabel(key)}>
                {oemFieldLabel(key)}
              </div>
              <div className="font-semibold text-gray-900 text-sm">{oemFieldValue(key, value)}</div>
            </div>
          ))}
        </div>
      )}
      {canControl && (
        <div className="mt-2">
          <div className="flex gap-2">
            <button
              type="button"
              disabled={!OEM_REMOTE_CONTROL_ENABLED || busy}
              title={controlTooltip}
              onClick={() => runCommand(true)}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-colors bg-green-50 text-green-700 border-green-200 hover:bg-green-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Lock className="w-4 h-4" /> Verrouiller
            </button>
            <button
              type="button"
              disabled={!OEM_REMOTE_CONTROL_ENABLED || busy}
              title={controlTooltip}
              onClick={() => runCommand(false)}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-colors bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Unlock className="w-4 h-4" /> Déverrouiller
            </button>
          </div>
          {!OEM_REMOTE_CONTROL_ENABLED && (
            <p className="mt-1 text-[11px] text-gray-400 text-center">
              Commandes à distance bientôt disponibles
            </p>
          )}
          {msg && <p className="mt-1 text-[11px] text-gray-500 text-center">{msg}</p>}
        </div>
      )}
    </div>
  );
}

// Escape user-controlled text before inserting it into innerHTML (prevents stored XSS).
function escapeHtml(value: string | null | undefined): string {
  return String(value ?? '').replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c,
  );
}

function createMarkerElement(device: Device): HTMLDivElement {
  const el = document.createElement('div');
  el.className = 'device-marker';

  const isOnline = device.status === 'ONLINE';
  const isPrivate = device.privacyActive === true;
  const isMoving = isOnline && !isPrivate && device.lastPosition && device.lastPosition.speed > 0;
  // Privacy wins over online/offline: it explains the silence, so the marker
  // reads as "deliberately not reporting" instead of a broken tracker.
  const color = isPrivate ? '#6366f1' : isOnline ? (device.markerColor || '#10b981') : '#64748b';
  const pulseColor = isMoving ? '#60A5FA' : '#22c55e';

  // Badge circle with plate label positioned absolutely at center,
  // then counter-rotated + translated to always appear above
  el.innerHTML = `
    <div style="position: relative; width: 32px; height: 32px;">
      ${device.plate ? `
        <div class="plate-label" style="
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, calc(-50% - 25px));
          background: rgba(0, 0, 0, 0.8);
          color: white;
          font-size: 10px;
          font-weight: 700;
          padding: 2px 6px;
          border-radius: 4px;
          white-space: nowrap;
          letter-spacing: 0.5px;
          line-height: 14px;
          pointer-events: none;
        ">${escapeHtml(device.plate)}</div>
      ` : ''}
      <div style="
        width: 32px;
        height: 32px;
        background: ${color};
        border-radius: 50%;
        border: 3px solid white;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <svg style="width: 14px; height: 14px;" viewBox="0 0 24 24" fill="white">
          <path d="M12 2L4 20h16L12 2z"/>
        </svg>
      </div>
      ${isOnline && !isPrivate ? `
        <div style="
          position: absolute;
          top: -2px;
          right: -2px;
          width: 10px;
          height: 10px;
          background: ${pulseColor};
          border-radius: 50%;
          ${isMoving ? 'animation: marker-ping 1s cubic-bezier(0, 0, 0.2, 1) infinite;' : ''}
        "></div>
      ` : ''}
    </div>
  `;

  return el;
}

